/**
 * What the Interview Candidates row says about putting somebody forward.
 *
 * The screen used to answer this with `hasSubmission`, which means "a
 * submission exists for them at all" and was rendered as the word "Tracked"
 * with no button beside it. That read as a terminal state and behaved like
 * one: the moment a candidate had been sent anywhere, the control to send
 * them somewhere else was gone.
 *
 * The scenario that broke on it is the ordinary one. Tata 1MG rejects a
 * candidate, so the recruiter puts them to Apollo. The server has always
 * allowed that — `open_submission_for` blocks a second attempt only at the
 * *same* client and only while the first is still running, and its docstring
 * says so in as many words: "a candidate rejected by a client six months ago
 * and put forward again today is a real second attempt and must be allowed".
 * Nothing in the database or the API stood in the way. The cell did.
 *
 * So the button is always offered once there is a call to submit from, and
 * the standing line beside it reports where they actually stand. Offering it
 * is safe rather than merely permitted: `POST /calls/{id}/submit` is
 * idempotent per client, returning the existing submission with 200 and
 * `already_tracked` rather than creating a second one. A recruiter who
 * presses it twice for one client gets the row they already had.
 *
 * "Open" here is the frontend's half of CLOSED_SUBMISSION_STATUSES. Two
 * spellings of one rule is how the screen and the server come to disagree
 * about whether somebody can be submitted, so the list is stated once, named
 * after the server's constant, and tested against it.
 */

/**
 * The statuses that mean the attempt is finished, either way.
 *
 * Mirrors `CLOSED_SUBMISSION_STATUSES` in `app/models/enums.py`. Rejected is
 * the client saying no; working is the candidate already placed and in the
 * job. Everything else — pending, shortlisted, selected, hold, followup,
 * yet_to_join — is an attempt still in flight.
 */
export const CLOSED_SUBMISSION_STATUSES = ['rejected', 'working'] as const;

/**
 * A submission status as a person reads it.
 *
 * Every other enum on these screens has had this and this one did not, so it
 * rendered as `status.replace(/_/g, ' ')` -- "yet to join" and "pending" in a
 * column beside "Final Round" and "Face to Face", which are labelled. The one
 * value the system generated was the one that looked unedited, which is the
 * same fault the call outcomes had.
 *
 * Mirrors `SubmissionStatus` in `app/models/enums.py`. All eight, because a
 * table renders what a row holds rather than what a picker offers.
 */
export const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  shortlisted: 'Shortlisted',
  selected: 'Selected',
  rejected: 'Rejected',
  hold: 'On Hold',
  followup: 'Follow Up',
  yet_to_join: 'Yet to Join',
  working: 'Working',
};

/**
 * The label, or a readable fallback.
 *
 * Falls back to the raw spelling rather than to a blank: if the enum grows
 * before this map does, an unlabelled value on screen is how somebody
 * notices. Same shape as interviewStatusLabel, deliberately -- the two sit in
 * one column, one behind the other.
 */
export function submissionStatusLabel(
  status: string | null | undefined,
): string | null {
  if (!status) return null;
  return SUBMISSION_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/** Whether a submission in this status is still running. */
export function isOpenSubmission(status: string | null | undefined): boolean {
  if (!status) return false;
  return !(CLOSED_SUBMISSION_STATUSES as readonly string[]).includes(status);
}

export interface SubmissionStanding {
  /**
   * Whether to offer the button.
   *
   * False only when there is no call to submit from — the endpoint is keyed
   * on the candidate's most recent call, so without one there is nothing to
   * post against. Never false merely because a submission already exists.
   */
  canSubmit: boolean;
  /**
   * The line shown above the button, or null when they have never been
   * put forward and there is nothing to report.
   */
  standing: string | null;
  /**
   * Whether `standing` describes a live attempt rather than a finished one.
   *
   * Drives the tone only. A closed standing is not a reason to withhold the
   * button; it is usually the reason somebody is pressing it.
   */
  open: boolean;
}

export interface SubmittableRow {
  latestCallId: string | null;
  latestSubmissionStatus: string | null;
  latestSubmissionClientName: string | null;
}

/**
 * Where this candidate stands, and whether the button is offered.
 *
 * The standing names the client, because "Tracked" answered a question
 * nobody asked. A recruiter looking at this cell wants to know who has them
 * and whether that is still live — "Rejected by Tata 1MG" says both, and
 * makes the next action obvious in a way a bare status word does not.
 *
 * Reads the *latest* submission, which is all the row carries: the list is
 * one row per person and `_latest_submission_for` resolves one submission for
 * it. A candidate with an older open attempt behind a newer closed one will
 * read as closed here. That is a real limitation and an acceptable one — the
 * button is offered either way, so the worst case is a standing line that
 * undersells the position, not a blocked action. The full history is on the
 * Joining Tracker, which is where the screen has always pointed for it.
 */
export function submissionStanding(row: SubmittableRow): SubmissionStanding {
  const canSubmit = Boolean(row.latestCallId);
  const status = row.latestSubmissionStatus;

  if (!status) {
    return { canSubmit, standing: null, open: false };
  }

  // Named where we can. A submission always has a client, but the name is
  // resolved through a separate lookup and a row whose client was merged away
  // can arrive without one -- so the sentence has to survive its absence
  // rather than rendering "Rejected by null".
  const client = row.latestSubmissionClientName;
  const open = isOpenSubmission(status);

  if (status === 'rejected') {
    return {
      canSubmit,
      standing: client ? `Rejected by ${client}` : 'Rejected',
      open: false,
    };
  }
  if (status === 'working') {
    return {
      canSubmit,
      standing: client ? `Working at ${client}` : 'Working',
      open: false,
    };
  }
  return {
    canSubmit,
    standing: client ? `With ${client}` : 'Submitted',
    open,
  };
}
